import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles/tw.merge";

import { InputGroup } from "@/components/Form/Base/inputGroup.component";
import { StoreSearchInput } from "@/components/Store/StoreSearchInput/storeSearchInput";
import {
    SearchItem,
    SearchItemType
} from "@/components/Store/StoreSearchInput/storeSearchInput.types";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useWoosmapServices } from "@/utils/hooks/useWoosmapServices.hook";

import { findLocalityDetails } from "../../queries/findLocalityDetails.query";
import {
    STORE_SEARCH_INPUT_TYPES,
    STORE_SEARCH_LOCATOR_MESSAGE_ID
} from "../../storeSearch.config";
import { useStoreSearchContext } from "../../storeSearch.context";

const Input = () => {
    const { t } = useGranite();

    const { LocalitiesService } = useWoosmapServices();

    const { selectedSearchItem, setSearchParams, resetSearchItem } =
        useStoreSearchContext();

    const handleOnUseLocation = (latitude: number, longitude: number) => {
        clearMessageById(STORE_SEARCH_LOCATOR_MESSAGE_ID);

        setSearchParams(
            {
                latitude: latitude,
                longitude: longitude
            },
            ["storeId"]
        );
    };

    const handleOnSelect = (newValue: SearchItem | null) => {
        if (!isDefined(LocalitiesService)) return;

        clearMessageById(STORE_SEARCH_LOCATOR_MESSAGE_ID);

        if (!newValue) {
            return setSearchParams({}, ["latitude", "longitude", "storeId"]);
        }

        if (newValue.type === SearchItemType.Store) {
            setSearchParams(
                {
                    storeId: newValue.value
                },
                ["latitude", "longitude"]
            );
        } else if (newValue.type === SearchItemType.Locality) {
            findLocalityDetails(LocalitiesService, newValue.value)
                .then(({ geometry }) => {
                    if (!isDefined(geometry)) return;

                    setSearchParams(
                        {
                            latitude: geometry.location.lat,
                            longitude: geometry.location.lng
                        },
                        ["storeId"]
                    );
                })
                .catch(() =>
                    broadcastMessage({
                        id: STORE_SEARCH_LOCATOR_MESSAGE_ID,
                        text: t("storesearch.locality.notFound"),
                        status: "danger",
                        dismissible: true
                    })
                );
        }
    };

    return (
        <StoreSearchInput
            clearable={true}
            menuPortalTarget={document.body}
            messageId={STORE_SEARCH_LOCATOR_MESSAGE_ID}
            value={selectedSearchItem}
            itemsToRetrieve={STORE_SEARCH_INPUT_TYPES}
            placeholder={t("form.searchstore.input.placeholder")}
            loadingText={t("form.searchstore.loading")}
            aria-label={t("accessibility.searchStore.input.label")}
            onClearValue={resetSearchItem}
            onSelect={handleOnSelect}
            onUseLocation={handleOnUseLocation}
        >
            <InputGroup>
                <StoreSearchInput.Input className={cn("tw:w-full")} />
                <StoreSearchInput.SearchButton>
                    <i className={cn("las la-lg la-search")} />
                </StoreSearchInput.SearchButton>
            </InputGroup>
            <StoreSearchInput.Location />
        </StoreSearchInput>
    );
};

export { Input as StoreSearchInput };
